import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type OnboardingProgressModuleKey = 'speed' | 'port' | 'emission' | 'cargo';

export type OnboardingModuleStatus = 'pending' | 'completed';

export interface OnboardingModuleDefinition {
  key: OnboardingProgressModuleKey;
  label: string;
  processLabel: string;
}

export interface OnboardingVesselContext {
  vessel_name: string;
  imo: string;
  vessel_type?: string;
  segment?: string;
  client_id?: number;
}

export interface OnboardingVesselSession extends OnboardingVesselContext {
  modules: Record<OnboardingProgressModuleKey, OnboardingModuleStatus>;
  updated_at: string;
}

export interface OnboardingProgressModuleView {
  key: OnboardingProgressModuleKey;
  label: string;
  processLabel: string;
  status: OnboardingModuleStatus;
}

export const ONBOARDING_PROGRESS_MODULES: ReadonlyArray<OnboardingModuleDefinition> = [
  {
    key: 'speed',
    label: 'Sailing Performance',
    processLabel: 'Process Sailing Performance',
  },
  {
    key: 'port',
    label: 'Port Performance',
    processLabel: 'Process Port Performance',
  },
  {
    key: 'emission',
    label: 'Emission',
    processLabel: 'Process Emission',
  },
  {
    key: 'cargo',
    label: 'Cargo Heating',
    processLabel: 'Process Cargo Heating',
  },
];

const STORAGE_KEY = 'onboarding-module-progress-sessions';
const FORM_DRAFT_KEY = 'onboarding-form-draft';

export interface OnboardingFormDraft {
  formValue: Record<string, unknown>;
  selectedModules: string[];
  updated_at: string;
}

@Injectable({
  providedIn: 'root',
})
export class OnboardingProgressService {
  private readonly sessions$ = new BehaviorSubject<
    Record<string, OnboardingVesselSession>
  >(this.loadAllSessions());

  getOrCreateSession(
    imo: string,
    context?: Partial<OnboardingVesselContext>,
  ): OnboardingVesselSession {
    const normalizedImo = this.normalizeImo(imo);
    const existing = this.sessions$.value[normalizedImo];
    if (existing) {
      const merged = this.mergeSessionContext(existing, context);
      if (merged !== existing) {
        this.persistSession(merged);
      }
      return merged;
    }

    const session: OnboardingVesselSession = {
      imo: normalizedImo,
      vessel_name: String(context?.vessel_name || '').trim(),
      vessel_type: context?.vessel_type,
      segment: context?.segment,
      client_id: context?.client_id,
      modules: {
        speed: 'pending',
        port: 'pending',
        emission: 'pending',
        cargo: 'pending',
      },
      updated_at: new Date().toISOString(),
    };
    this.persistSession(session);
    return session;
  }

  getSession(imo: string): OnboardingVesselSession | null {
    const normalizedImo = this.normalizeImo(imo);
    return this.sessions$.value[normalizedImo] ?? null;
  }

  getModuleViews(imo: string): OnboardingProgressModuleView[] {
    const session = this.getOrCreateSession(imo);
    return ONBOARDING_PROGRESS_MODULES.map((module) => ({
      ...module,
      status: session.modules[module.key],
    }));
  }

  markModuleCompleted(
    imo: string,
    module: OnboardingProgressModuleKey,
    context?: Partial<OnboardingVesselContext>,
  ): OnboardingVesselSession {
    const session = this.getOrCreateSession(imo, context);
    const updated: OnboardingVesselSession = {
      ...session,
      ...this.pickContext(context),
      modules: {
        ...session.modules,
        [module]: 'completed',
      },
      updated_at: new Date().toISOString(),
    };
    this.persistSession(updated);
    return updated;
  }

  isAllCompleted(imo: string): boolean {
    const session = this.getSession(imo);
    if (!session) {
      return false;
    }
    return ONBOARDING_PROGRESS_MODULES.every(
      (module) => session.modules[module.key] === 'completed',
    );
  }

  saveVesselContext(imo: string, context: OnboardingVesselContext): void {
    this.getOrCreateSession(imo, context);
  }

  saveFormDraft(
    draft: Pick<OnboardingFormDraft, 'formValue' | 'selectedModules'>,
  ): void {
    const payload: OnboardingFormDraft = {
      formValue: draft.formValue,
      selectedModules: [...draft.selectedModules],
      updated_at: new Date().toISOString(),
    };
    sessionStorage.setItem(FORM_DRAFT_KEY, JSON.stringify(payload));
  }

  getFormDraft(): OnboardingFormDraft | null {
    const raw = sessionStorage.getItem(FORM_DRAFT_KEY);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as OnboardingFormDraft;
      if (!parsed?.formValue || typeof parsed.formValue !== 'object') {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  clearFormDraft(): void {
    sessionStorage.removeItem(FORM_DRAFT_KEY);
  }

  private mergeSessionContext(
    session: OnboardingVesselSession,
    context?: Partial<OnboardingVesselContext>,
  ): OnboardingVesselSession {
    if (!context) {
      return session;
    }

    const merged: OnboardingVesselSession = {
      ...session,
      ...this.pickContext(context),
      updated_at: new Date().toISOString(),
    };
    return merged;
  }

  private pickContext(
    context?: Partial<OnboardingVesselContext>,
  ): Partial<OnboardingVesselContext> {
    if (!context) {
      return {};
    }

    return {
      ...(context.vessel_name ? { vessel_name: context.vessel_name } : {}),
      ...(context.imo ? { imo: this.normalizeImo(context.imo) } : {}),
      ...(context.vessel_type ? { vessel_type: context.vessel_type } : {}),
      ...(context.segment ? { segment: context.segment } : {}),
      ...(context.client_id ? { client_id: context.client_id } : {}),
    };
  }

  private persistSession(session: OnboardingVesselSession): void {
    const next = {
      ...this.sessions$.value,
      [session.imo]: session,
    };
    this.sessions$.next(next);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }

  private loadAllSessions(): Record<string, OnboardingVesselSession> {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }

    try {
      const parsed = JSON.parse(raw) as Record<string, OnboardingVesselSession>;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private normalizeImo(imo: string): string {
    return String(imo || '').replace(/\D/g, '').trim();
  }
}
